import { Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, OnInit, ViewChild, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { Observable, combineLatest, from, of, timer } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { Agent, AgentApiResponse } from '../../../domain/interfaces/agents';
import { map, tap, concatMap, groupBy, mergeMap, toArray, share, shareReplay, catchError } from 'rxjs/operators';
import { LoginService } from '@uoa/auth';
import peelPage from '../../../core/pipes/peel-first-page.operator';
import { NgClass, AsyncPipe } from '@angular/common';
import { IonicSlides } from '@ionic/angular';
import { StatusSummaryComponent } from '../../wallboard/status-summary/status-summary.component';
import { CardFrameComponent } from '../../wallboard/card-frame/card-frame.component';
import { AgentCardComponent } from '../../wallboard/agent-card/agent-card.component';

interface QueueStat {
  queueName: string;
  queueId: string;
  callsWaiting?: number;
  totalCalls?: number;
  GOS?: number;
  ASA?: number;
  contactsAbandoned?: number;
  contactsHandled?: number;
  callbacksHandled?: number;
  oldestContact?: number;
  oldestContactOverThreshold?: boolean;
}

interface QueueSummary {
  totalCalls?: number;
  GOS?: number;
  ASA?: number;
  contactsAbandoned?: number;
  contactsHandled?: number;
  callbacksHandled?: number;
  callsWaiting?: number;
  oldestContact?: number;
  callsWaitingOverThreshold?: boolean;
  gosOverThreshold?: boolean;
}

interface EmailReportData {
  rows?: number[][];
}

interface StatsApiResponse {
  data: QueueStat[];
  dataSummary?: QueueSummary;
  emailReportData?: EmailReportData;
}

interface DisplayQueueStat extends Omit<QueueStat, 'oldestContact'> {
  oldestContact: number | string;
}

interface DisplayQueueSummary extends QueueSummary {
  callsWaitingOverThreshold: boolean;
  gosOverThreshold: boolean;
}

interface StatsViewModel {
  data: DisplayQueueStat[];
  dataSummary: DisplayQueueSummary | null;
  totalEmails: number;
}

interface RequestOptions {
  headers?: {
    'x-api-key': string;
  };
}

type SwiperContainerElement = HTMLElement & {
  initialize: () => void;
  swiper?: {
    update: () => void;
    autoplay?: {
      start: () => void;
    };
  };
};

const sortOrder = (statusIdentifier: string) => {
  switch (statusIdentifier) {
    case 'inbound':
      return 1;
    case 'emails':
      return 2;
    case 'webchat':
      return 3;
    case 'auckland online':
      return 4;
    case 'outbound':
      return 5;
    case 'break':
      return 6;
    case 'team leader':
      return 7;
    default:
      return 8;
  }
};

const formatDuration = (seconds: number, includeHours: boolean): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  if (includeHours) {
    return [hours, minutes, remainingSeconds].map((value) => String(value).padStart(2, '0')).join(':');
  }

  return [minutes, remainingSeconds].map((value) => String(value).padStart(2, '0')).join(':');
};

@Component({
  selector: 'app-stats-board',
  templateUrl: './stats-board.component.html',
  styleUrls: ['./stats-board.page1.component.scss'],
  imports: [NgClass, StatusSummaryComponent, CardFrameComponent, AgentCardComponent, AsyncPipe],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class StatsBoardComponent implements OnInit {
  private httpClient = inject(HttpClient);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private loginService = inject(LoginService);
  private swiperElement?: SwiperContainerElement;
  private swiperInitialized = false;

  @ViewChild('statsSwiper')
  set statsSwiper(elementRef: ElementRef<SwiperContainerElement> | undefined) {
    this.swiperElement = elementRef?.nativeElement;
    this.syncSwiper();
  }

  stats$!: Observable<StatsViewModel>;
  statsTitle = '';
  agentsTitle = '';

  agents$!: Observable<Agent[][]>;
  agentPages$!: Observable<Agent[][][]>;
  swiperModules = [IonicSlides];
  swiperOptions = {
    modules: [IonicSlides],
    speed: 1000,
    allowTouchMove: false,
    effect: 'fade',
    autoplay: { delay: 20000 },
    fadeEffect: { crossFade: true },
  };

  available = 0;
  notAvailable = 0;
  outOfOffice = 0;

  statusCompare(a: Agent[], b: Agent[]): number {
    return sortOrder(a[0].CurrentEvent.Status.Item.Identifier) - sortOrder(b[0].CurrentEvent.Status.Item.Identifier);
  }

  dateCompare(a: Agent, b: Agent): number {
    return new Date(a.CurrentEvent.Date).getTime() - new Date(b.CurrentEvent.Date).getTime();
  }

  ngOnInit() {
    this.route.data.subscribe((api) => {
      console.log('Got API: ', api);

      if (environment.wallboards.staff.stats.mockData) {
        this.stats$ = of(environment.wallboards.staff.stats.mockData as StatsApiResponse).pipe(map(this.mapStats));
        console.log('Using stats mock data: ', environment.wallboards.staff.stats.mockData);
        this.statsTitle = api.stats?.title;
      } else {
        let options: RequestOptions = {};
        let urlSuffix = '';
        const apikey = this.route.snapshot.params['key'];
        if (apikey) {
          options = {
            headers: {
              'x-api-key': apikey,
            },
          };
          urlSuffix = '-tv';
        }
        this.statsTitle = api.stats?.title;
        // soft reload timer
        this.stats$ = timer(0, 4000).pipe(
          // workaround until auth library can handle `x-amzn-errortype: UnauthorizedException` header
          tap(async () => {
            if (!apikey) {
              await this.loginService.doLogin(this.router.url);
            }
          }),
          concatMap(() =>
            this.httpClient.get<StatsApiResponse>(api.stats.baseUrl + urlSuffix, options).pipe(
              tap((s) => {
                console.log('Got stats data: ', s);
                console.log('Reloading', s);
              }),
              map(this.mapStats),
              tap((s) => console.log(s)),
              catchError((error) => {
                if (error.status === 429) {
                  console.warn('API is rate limited. You may need to adjust the rate limiting threshold.');
                  return of<StatsViewModel>({ data: [], dataSummary: null, totalEmails: 0 });
                }
                return of<StatsViewModel>({ data: [], dataSummary: null, totalEmails: 0 });
              }),
            ),
          ),
        );
      }

      if (environment.wallboards.staff.agents.mockData) {
        console.log('Using agents mock data: ', environment.wallboards.staff.agents.mockData);
        const mockAgents = environment.wallboards.staff.agents.mockData as AgentApiResponse;
        // ascending by status, descending by time
        this.agents$ = of(mockAgents).pipe(
          // agents
          tap(this.tapAgents),
          map(this.filterAgents),
          // split array into separate emitted objects
          mergeMap((s: Agent[]) => from(s)),
          // group objects together by identifier
          groupBy((s: Agent) => s.CurrentEvent.Status.Item.GroupName),
          // convert each group to arrays
          mergeMap((group) => group.pipe(toArray())),
          // sort within each group: date descending
          //tap(c => console.info(c)),
          map((agentsInGroup: Agent[]) => agentsInGroup.sort(this.dateCompare)),
          // do the opposite of the split above, and emit a single array (of arrays)
          toArray(),
          map((groupedAgents: Agent[][]) => groupedAgents.sort(this.statusCompare)),

          catchError(() => of([])),
        );
        this.subscribeAgents();
      } else {
        let options: RequestOptions = {};
        let urlSuffix = '';
        const apikey = this.route.snapshot.params['key'];
        if (apikey) {
          options = {
            headers: {
              'x-api-key': apikey,
            },
          };
          urlSuffix = '-tv';
        }
        console.log('Getting agents');
        // soft reload timer
        this.agents$ = timer(0, 8000).pipe(
          // workaround until auth library can handle `x-amzn-errortype: UnauthorizedException` header
          tap(async () => {
            if (!apikey) {
              await this.loginService.doLogin(this.router.url);
            }
          }),
          concatMap(() =>
            this.httpClient.get<AgentApiResponse>(api.agents.baseUrl + urlSuffix, options).pipe(
              // agents
              tap(this.tapAgents),
              map(this.filterAgents),
              // split array into separate emitted objects
              mergeMap((s: Agent[]) => from(s)),
              // group objects together by identifier
              groupBy((s: Agent) => s.CurrentEvent.Status.Item.GroupName),
              // convert each group to arrays
              mergeMap((group) => group.pipe(toArray())),
              // sort within each group: date descending
              //tap(c => console.info(c)),
              map((agentsInGroup: Agent[]) => agentsInGroup.sort(this.dateCompare)),
              // do the opposite of the split above, and emit a single array (of arrays)
              toArray(),
              map((groupedAgents: Agent[][]) => groupedAgents.sort(this.statusCompare)),

              catchError(() => of([])),
            ),
          ),
        );

        this.subscribeAgents();
      }

      this.scheduleHardReload();
    });
  }

  protected scheduleHardReload() {
    window.setTimeout(
      () => {
        window.location.reload();
      },
      1000 * 60 * 60,
    );
  }

  mapStats = (s: StatsApiResponse): StatsViewModel => ({
    data: s.data
      .filter((n) => !n.queueName.toLowerCase().includes('unused'))
      .map((q) => {
        const oldestContactSeconds = q.oldestContact ?? 0;
        const oldestContactOverThreshold = q.oldestContactOverThreshold ?? oldestContactSeconds >= 3600;

        return {
          ...q,
          oldestContactOverThreshold,
          oldestContact: q.oldestContact ? formatDuration(oldestContactSeconds, oldestContactOverThreshold) : '-',
          queueName: q.queueName.replace('Queue', ''),
        };
      }),
    dataSummary: s.dataSummary
      ? {
          ...s.dataSummary,
          callsWaitingOverThreshold: (s.dataSummary.callsWaiting ?? 0) > 5,
          gosOverThreshold: (s.dataSummary.GOS ?? 0) < 80 && (s.dataSummary.GOS ?? 0) >= 0.01,
        }
      : null,
    totalEmails: s.emailReportData?.rows ? s.emailReportData.rows[0][0] : 0,
  });

  filterAgents = (response: AgentApiResponse) =>
    response.data.Items.filter(
      (agent) =>
        agent.CurrentEvent?.Status?.Item?.Visible &&
        agent.CurrentEvent?.Status?.Item?.Visible !== 'false' &&
        agent.FullAgentName &&
        !agent.FullAgentName.includes('IAM'),
    );

  tapAgents = (response: AgentApiResponse) => {
    console.log('Got agent data: ', response);
    console.log('Updating board using', response.data?.Items);
    this.available = response.data?.Items.filter(
      (item) =>
        item.CurrentEvent?.Status?.Item?.Colour.toLowerCase() == 'green' &&
        (item.CurrentEvent?.Status?.Item?.Visible === true || item.CurrentEvent?.Status?.Item?.Visible === 'true'),
    ).length;
    this.notAvailable = response.data?.Items.filter(
      (item) =>
        item.CurrentEvent?.Status?.Item?.Colour.toLowerCase() == 'orange' &&
        (item.CurrentEvent?.Status?.Item?.Visible === true || item.CurrentEvent?.Status?.Item?.Visible === 'true'),
    ).length;
    this.outOfOffice = response.data?.Items.filter(
      (item) =>
        item.CurrentEvent?.Status?.Item?.Colour.toLowerCase() == 'red' &&
        (item.CurrentEvent?.Status?.Item?.Visible === true || item.CurrentEvent?.Status?.Item?.Visible === 'true'),
    ).length;
  };

  subscribeAgents = () => {
    this.agents$.subscribe((agentGroups) => console.log('Agent groups: ', agentGroups));

    // avoid doing http call multiple times
    const sharedAgentGroups = this.agents$.pipe(share());

    this.agentPages$ = combineLatest([
      sharedAgentGroups.pipe((s) => peelPage(s, 1)),
      sharedAgentGroups.pipe((s) => peelPage(s, 2)),
      sharedAgentGroups.pipe((s) => peelPage(s, 3)),
      sharedAgentGroups.pipe((s) => peelPage(s, 4)),
    ]).pipe(
      map((pages) => pages.filter((page) => page.length > 0)),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.agentPages$.subscribe(() => {
      queueMicrotask(() => this.syncSwiper());
    });
  };

  private syncSwiper() {
    if (!this.swiperElement) {
      return;
    }

    if (!this.swiperInitialized) {
      if (typeof this.swiperElement.initialize === 'function') {
        Object.assign(this.swiperElement, this.swiperOptions);
        this.swiperElement.initialize();
        this.swiperElement.swiper?.autoplay?.start();
        this.swiperInitialized = true;
      } else {
        return;
      }
    }

    this.swiperElement.swiper?.update();
  }
}
